import { createAppRequest } from "@/services/request";
import type {
  ReceiptCapabilities,
  ReceiptContractContext,
  ReceiptPage,
  ReceiptPlan,
  ReceiptRegistration,
  ReceiptSummary,
  ReceiptTransaction,
  ReceiptWorkflowQuery,
  SaveReceiptPlan,
  SaveReceiptRegistration,
} from "./types/receipt-workflow";
const request = createAppRequest();
const BASE = "/bff/zy-crm";
export const enableReceiptPlanGroup = (
  contractUuid: string,
  revision: number,
  clientRequestId: string,
) =>
  request<ReceiptContractContext>(`${BASE}/receipt-schedules/enable-group`, {
    method: "POST",
    data: { contractUuid, revision, clientRequestId },
  });
export const syncReceiptProjections = (rebuild = false) =>
  request<{
    initialized: boolean;
    checkpoints: { center: string; last_error?: string }[];
  }>(`${BASE}/receipt-workflow/${rebuild ? "rebuild" : "sync"}`, {
    method: "POST",
  });
export const getReceiptSyncStatus = () =>
  request<{
    initialized: boolean;
    checkpoints: {
      center: string;
      last_error?: string;
      last_synced_at?: string;
      last_reconciled_at?: string;
    }[];
  }>(`${BASE}/receipt-workflow/sync`);
export const getReceiptFinanceLink = (uuid: string) =>
  request<{ url: string }>(`${BASE}/receipt-transactions/${uuid}/finance-link`);
export const getReceiptCapabilities = () =>
  request<ReceiptCapabilities>(`${BASE}/receipt-workflow/capabilities`);
export const getReceiptContracts = (params: ReceiptWorkflowQuery) =>
  request<
    ReceiptPage<{
      uuid: string;
      contractName: string;
      contractNo: string;
      customerUuid: string;
    }>
  >(`${BASE}/receipt-workflow/contracts`, { params });
export const getReceiptContext = (uuid: string) =>
  request<ReceiptContractContext>(
    `${BASE}/receipt-workflow/contracts/${uuid}/context`,
  );
export const queryReceiptContractSummaries = (params: ReceiptWorkflowQuery) =>
  request<
    ReceiptPage<import("./types/receipt-workflow").ReceiptContractSummary>
  >(`${BASE}/receipt-contract-summaries`, { params });
export const getReceiptSummary = (params: ReceiptWorkflowQuery) =>
  request<ReceiptSummary>(`${BASE}/receipt-summary`, { params });
export const queryReceiptRegistrations = (params: ReceiptWorkflowQuery) =>
  request<ReceiptPage<ReceiptRegistration>>(`${BASE}/receipt-registrations`, {
    params,
  });
export const queryReceiptTransactions = (params: ReceiptWorkflowQuery) =>
  request<ReceiptPage<ReceiptTransaction>>(`${BASE}/receipt-transactions`, {
    params,
  });
export const queryWorkflowPlans = (params: ReceiptWorkflowQuery) =>
  request<ReceiptPage<ReceiptPlan>>(`${BASE}/receipt-workflow/plans`, {
    params,
  });
export const getWorkflowPlan = (uuid: string) =>
  request<ReceiptPlan>(`${BASE}/receipt-workflow/plans/${uuid}`);
export const getRegistration = (uuid: string) =>
  request<ReceiptRegistration>(`${BASE}/receipt-registrations/${uuid}`);
export const getTransaction = (uuid: string) =>
  request<ReceiptTransaction>(`${BASE}/receipt-transactions/${uuid}`);
export const saveRegistration = (
  data: SaveReceiptRegistration,
  uuid?: string,
) =>
  request<ReceiptRegistration>(
    `${BASE}/receipt-registrations${uuid ? `/${uuid}` : ""}`,
    { method: uuid ? "PATCH" : "POST", data },
  );
export const saveReceiptPlan = (data: SaveReceiptPlan, uuid?: string) =>
  request<ReceiptPlan>(`${BASE}/receipt-schedules${uuid ? `/${uuid}` : ""}`, {
    method: uuid ? "PATCH" : "POST",
    data,
  });
export const registrationAction = (
  uuid: string,
  action: "submit" | "withdraw" | "delete",
  revision: number,
  clientRequestId: string,
) =>
  request<ReceiptRegistration>(
    `${BASE}/receipt-registrations/${uuid}${action === "delete" ? "" : `/${action}`}`,
    {
      method: action === "delete" ? "DELETE" : "POST",
      data: { revision, clientRequestId },
    },
  );
export const receiptPlanAction = (
  uuid: string,
  action: "enable" | "cancel" | "delete",
  revision: number,
  clientRequestId: string,
) =>
  request<ReceiptPlan>(
    `${BASE}/receipt-schedules/${uuid}${action === "delete" ? "" : `/${action}`}`,
    {
      method: action === "delete" ? "DELETE" : "POST",
      data: { revision, clientRequestId },
    },
  );
export const getReceiptRequestResult = (
  uuid: string,
  center: "contract" | "finance" = "finance",
) =>
  request<{
    status: "NOT_FOUND" | "PENDING" | "SUCCEEDED" | "FAILED";
    result?: ReceiptRegistration | ReceiptPlan;
    message?: string;
  }>(`${BASE}/receipt-workflow/requests/${uuid}`, { params: { center } });
